var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import * as did from '../did/local';
import { verifySignedData } from '../did/validation';
import * as crypto from '../crypto';
import { base64 } from '../common';
/**
 * Create a UCAN, User Controlled Authorization Networks, JWT.
 * This JWT can be used for authorization.
 *
 * ### Header
 *
 * `alg`, Algorithm, the type of signature.
 * `typ`, Type, the type of this data structure, JWT.
 * `uav`, UCAN version.
 *
 * ### Payload
 *
 * `aud`, Audience, the ID of who it's intended for.
 * `exp`, Expiry, unix timestamp of when the jwt is no longer valid.
 * `iss`, Issuer, the ID of who sent this.
 * `nbf`, Not Before, unix timestamp of when the jwt becomes valid.
 * `prf`, Proof, an optional nested token with equal or greater privileges.
 * `ptc`, Potency, which rights come with the token.
 * `rsc`, Resource, the involved resource.
 *
 */
export function build(_a) {
    var _b = _a.addSignature, addSignature = _b === void 0 ? true : _b, audience = _a.audience, _c = _a.facts, facts = _c === void 0 ? [] : _c, issuer = _a.issuer, _d = _a.lifetimeInSeconds, lifetimeInSeconds = _d === void 0 ? 30 : _d, expiration = _a.expiration, _e = _a.potency, potency = _e === void 0 ? 'APPEND' : _e, proof = _a.proof, resource = _a.resource;
    return __awaiter(this, void 0, void 0, function () {
        var currentTimeInSeconds, decodedProof, ksAlg, header, exp, nbf, prf, payload, _f, signature, _g;
        var _h;
        return __generator(this, function (_j) {
            switch (_j.label) {
                case 0:
                    currentTimeInSeconds = Math.floor(Date.now() / 1000);
                    decodedProof = proof && decode(proof);
                    return [4 /*yield*/, crypto.keystore.getAlg()
                        // Header
                    ];
                case 1:
                    ksAlg = _j.sent();
                    header = {
                        alg: jwtAlgorithm(ksAlg) || 'UnknownAlgorithm',
                        typ: 'JWT',
                        uav: '1.0.0' // actually 0.3.1 but server isn't updated yet
                    };
                    exp = expiration || (currentTimeInSeconds + lifetimeInSeconds);
                    nbf = currentTimeInSeconds - 60;
                    if (decodedProof) {
                        prf = decodedProof.payload;
                        exp = Math.min(prf.exp, exp);
                        nbf = Math.max(prf.nbf, nbf);
                    }
                    _h = {
                        aud: audience,
                        exp: exp,
                        fct: facts
                    };
                    _f = issuer;
                    if (_f) return [3 /*break*/, 3];
                    return [4 /*yield*/, did.ucan()];
                case 2:
                    _f = (_j.sent());
                    _j.label = 3;
                case 3:
                    payload = (_h.iss = _f,
                        _h.nbf = nbf,
                        _h.prf = proof || null,
                        _h.ptc = potency,
                        _h.rsc = resource ? resource : (decodedProof ? decodedProof.payload.rsc : '*'),
                        _h);
                    if (!addSignature) return [3 /*break*/, 5];
                    return [4 /*yield*/, sign(header, payload)];
                case 4:
                    _g = _j.sent();
                    return [3 /*break*/, 6];
                case 5:
                    _g = null;
                    _j.label = 6;
                case 6:
                    signature = _g;
                    return [2 /*return*/, {
                            header: header,
                            payload: payload,
                            signature: signature
                        }];
            }
        });
    });
}
/**
 * Try to decode a UCAN.
 * Will throw if it fails.
 *
 * @param ucan The encoded UCAN to decode
 */
export function decode(ucan) {
    var split = ucan.split(".");
    var header = JSON.parse(base64.urlDecode(split[0]));
    var payload = JSON.parse(base64.urlDecode(split[1]));
    return {
        header: header,
        payload: payload,
        signature: split[2] || null
    };
}
/**
 * Encode a UCAN.
 *
 * @param ucan The UCAN to encode
 */
export function encode(ucan) {
    var encodedHeader = encodeHeader(ucan.header);
    var encodedPayload = encodePayload(ucan.payload);
    return encodedHeader + '.' +
        encodedPayload + '.' +
        ucan.signature;
}
/**
 * Encode the header of a UCAN.
 *
 * @param header The UcanHeader to encode
 */
export function encodeHeader(header) {
    return base64.urlEncode(JSON.stringify(header));
}
/**
 * Encode the payload of a UCAN.
 *
 * @param payload The UcanPayload to encode
 */
export function encodePayload(payload) {
    return base64.urlEncode(JSON.stringify(__assign({}, payload)));
}
/**
 * Check if a UCAN is expired.
 *
 * @param ucan The UCAN to validate
 */
export function isExpired(ucan) {
    return ucan.payload.exp <= Math.floor(Date.now() / 1000);
}
/**
 * Check if a UCAN is valid.
 *
 * @param ucan The decoded UCAN
 * @param did The DID associated with the signature of the UCAN
 */
export function isValid(ucan) {
    return __awaiter(this, void 0, void 0, function () {
        var encodedHeader, encodedPayload, a, prf, b;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    encodedHeader = encodeHeader(ucan.header);
                    encodedPayload = encodePayload(ucan.payload);
                    return [4 /*yield*/, verifySignedData({
                            charSize: 8,
                            data: encodedHeader + "." + encodedPayload,
                            did: ucan.payload.iss,
                            signature: base64.makeUrlUnsafe(ucan.signature || "")
                        })];
                case 1:
                    a = _a.sent();
                    if (!a)
                        return [2 /*return*/, a];
                    if (!ucan.payload.prf)
                        return [2 /*return*/, true
                            // Verify proofs
                        ];
                    prf = decode(ucan.payload.prf);
                    b = prf.payload.aud === ucan.payload.iss;
                    if (!b)
                        return [2 /*return*/, b];
                    return [4 /*yield*/, isValid(prf)];
                case 2: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/**
 * Given a UCAN, lookup the root issuer.
 *
 * Throws when given an improperly formatted UCAN.
 * This could be a nested UCAN (ie. proof).
 *
 * @param ucan A UCAN.
 * @returns The root issuer.
 */
export function rootIssuer(ucan, level) {
    if (level === void 0) { level = 0; }
    var p = extractPayload(ucan, level);
    if (p.prf)
        return rootIssuer(p.prf, level + 1);
    return p.iss;
}
/**
 * Generate UCAN signature.
 */
export function sign(header, payload) {
    return __awaiter(this, void 0, void 0, function () {
        var encodedHeader, encodedPayload, _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    encodedHeader = encodeHeader(header);
                    encodedPayload = encodePayload(payload);
                    _b = (_a = base64).makeUrlSafe;
                    return [4 /*yield*/, crypto.keystore.sign(encodedHeader + "." + encodedPayload, 8)];
                case 1: return [2 /*return*/, _b.apply(_a, [_c.sent()])];
            }
        });
    });
}
// ㊙️
/**
 * JWT algorithm to be used in a JWT header.
 */
function jwtAlgorithm(cryptoSystem) {
    switch (cryptoSystem) {
        case "ed25519": return 'EdDSA';
        case "rsa": return 'RS256';
        default: return null;
    }
}
/**
 * Extract the payload of a UCAN.
 *
 * Throws when given an improperly formatted UCAN.
 */
function extractPayload(ucan, level) {
    try {
        return JSON.parse(base64.urlDecode(ucan.split(".")[1]));
    }
    catch (_) {
        throw new Error("Invalid UCAN (" + level + " level" + (level === 1 ? "" : "s") + " deep): `" + ucan + "`");
    }
}
//# sourceMappingURL=token.js.map